import React from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * AuthCallbackGuard — dead-end guard for /auth/callback without valid auth state.
 *
 * If the user lands on /auth/callback without a valid `code` parameter,
 * show a meaningful error message with a "Go to login" link instead of
 * a blank page or silent redirect loop.
 */
export default function AuthCallbackGuard() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const error = searchParams.get('error');

  // Valid auth callback — has a code, will be handled by AuthContext
  if (code) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Authenticating...
      </div>
    );
  }

  // Auth provider returned an error
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        fontFamily: "'Inter', system-ui, sans-serif"
      }}>
        <h1 style={{ color: '#d32f2f', marginBottom: '1rem' }}>
          Authentication Failed
        </h1>
        <p style={{ color: '#666', marginBottom: '2rem', maxWidth: '400px', textAlign: 'center' }}>
          {error === 'access_denied'
            ? 'You denied the authentication request. Please try again.'
            : `An error occurred during authentication: ${error}`}
        </p>
        <a
          href="/login"
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#1976d2',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '4px',
            fontWeight: 500
          }}
        >
          Go to Login
        </a>
      </div>
    );
  }

  // No code, no error — landed on callback without auth state
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: "'Inter', system-ui, sans-serif"
    }}>
      <h1 style={{ color: '#666', marginBottom: '1rem' }}>
        Invalid Authentication Request
      </h1>
      <p style={{ color: '#999', marginBottom: '2rem', maxWidth: '400px', textAlign: 'center' }}>
        You reached the authentication callback page without a valid login request.
        This usually happens if you navigated here directly or your session expired.
      </p>
      <a
        href="/login"
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: '#1976d2',
          color: 'white',
          textDecoration: 'none',
          borderRadius: '4px',
          fontWeight: 500
        }}
      >
        Go to Login
      </a>
    </div>
  );
}
